import { FC, useEffect, useState } from 'react';
import { LoadingOutlined } from '@ant-design/icons';
import { Button, Checkbox, Divider, Dropdown, Menu } from 'antd';
import { capitalCase } from 'change-case';
import { TbInfoCircle, TbPackageExport, TbSettings2 } from 'react-icons/tb';

import { useAntDesignTheme } from '@/hooks/useAntDesignTheme';
import { useEntitlements } from '@/hooks/useEntitlements';
import { DropdownMenuPanel } from '@/shared';
import DropdownButton from '@/shared/components/dropdowns/DropdownButton';
import { toast } from '@/shared/components/toasts/PlatformToast';
import { ellipsisStyling } from '@/utilities';

import { companySearchResultsGridCellStyle, getCompanySearchResultsGridStyle } from '../layout';
import ColumnDropdownFilters from './ColumnDropdownFilters';

interface ListHeaderProps {
    availableColumns: any;
    listColumns: any;
    setListViewColumns: any;
    results: any;
    resultsSelected: any;
    setResultsSelected: any;
    indeterminate: any;
    checkAll: any;
    handleCheckAllChange: any;
}

const ListHeader: FC<ListHeaderProps> = ({
    availableColumns,
    listColumns,
    setListViewColumns,
    results,
    resultsSelected,
    setResultsSelected,
    indeterminate,
    checkAll,
    handleCheckAllChange,
}): JSX.Element => {
    const [loading, setLoading] = useState(true);

    useEffect(() => {
        setLoading(false);
    }, []);

    const { hasFeature } = useEntitlements();
    const { token } = useAntDesignTheme();

    const columnIconMap = {} as any;

    const handleMenuClick = (index: number, newValue: string) => {
        setLoading(true);

        const updatedArray = [...listColumns];

        updatedArray[index] = newValue; // Replace the value at the specified index

        setListViewColumns(updatedArray);
        setLoading(false);
    };

    // Helper so that we can access all of the Clearbit properties
    function combineSubobjects(obj: any) {
        if (!obj || typeof obj !== 'object') {
            return {};
        } // Handle invalid input

        return Object.assign({}, ...Object.values(obj));
    }

    // Function to convert data to CSV format
    const exportToCSV = () => {
        // Extract headers
        const totalHeaders = ['companyName', 'website', ...listColumns];

        // turns our results array into just an object array of enrichment values
        const newResults =
            results?.length !== 0
                ? results.map((item: any) => {
                      const { enrichment, ...rest } = item;

                      const clearbit = enrichment?.clearbit && combineSubobjects(enrichment?.clearbit);

                      return {
                          name: rest.name, // Keep the name
                          ...clearbit,
                          ...enrichment, // Spread enrichment properties
                      };
                  })
                : [];

        const headers = totalHeaders.map((col: any) => col).join(',');

        // Extract rows
        const rows = newResults?.map((row: any) =>
            totalHeaders
                .map((col: any) => {
                    const value = row[col as keyof typeof row];
                    return `"${value ?? ''}"`; // Wrap in quotes to handle commas
                })
                .join(','),
        );

        // Combine headers and rows
        const csvContent = [headers, ...rows].join('\n');

        toast('Exporting...', {
            icon: <LoadingOutlined spin />,
        });

        // Create a Blob and trigger download
        const blob = new Blob([csvContent], { type: 'text/csv;charset=utf-8;' });
        const url = URL.createObjectURL(blob);
        const link = document.createElement('a');
        link.href = url;
        link.setAttribute('download', `Company-Search-Results-${new Date()}.csv`);
        document.body.appendChild(link);
        link.click();
        document.body.removeChild(link);
    };

    return (
        <div
            style={{
                ...getCompanySearchResultsGridStyle(listColumns),
                backgroundColor: token.colorBgBase,
                border: `1px solid ${token.colorBorder}`,
                borderRadius: token.borderRadius,
                padding: '0.25rem',
            }}
        >
            <div style={{ ...companySearchResultsGridCellStyle, display: 'flex', alignItems: 'center' }}>
                <span data-selection-control='true' style={{ display: 'inline-flex', marginLeft: '0.25rem' }}>
                    <Checkbox
                        indeterminate={indeterminate}
                        checked={checkAll}
                        onChange={(e) => handleCheckAllChange(e.target.checked)}
                    />
                </span>
                <Divider type='vertical' />
                <span
                    style={{
                        padding: '0 0.25rem',
                        marginRight: '0.25rem',
                        verticalAlign: 0,
                    }}
                >
                    <TbInfoCircle style={{ verticalAlign: '-0.125rem', color: token.colorTextTertiary }} />
                    <span style={{ color: token.colorTextTertiary, marginLeft: '0.375rem' }}>Search Results</span>
                </span>
            </div>
            {listColumns.map((col: string, index: number) => (
                <div
                    key={col}
                    style={{
                        ...companySearchResultsGridCellStyle,
                        textAlign: 'right',
                        overflow: 'hidden',
                        textOverflow: 'ellipsis',
                    }}
                >
                    <ColumnDropdownFilters
                        col={col}
                        index={index}
                        availableColumns={availableColumns}
                        handleMenuClick={handleMenuClick}
                        listColumns={listColumns}
                    >
                        <DropdownButton size='small' type='text' style={{ ...ellipsisStyling }}>
                            {col && columnIconMap[col]}
                            {col && capitalCase(col)}
                        </DropdownButton>
                    </ColumnDropdownFilters>
                </div>
            ))}
            <div style={{ ...companySearchResultsGridCellStyle, textAlign: 'right' }}>
                <Dropdown
                    trigger={['click']}
                    placement='bottomRight'
                    popupRender={() => (
                        <DropdownMenuPanel>
                            <small style={{ marginLeft: '0.75rem', color: token.colorTextTertiary }}>
                                Result Options
                            </small>
                            <Menu.Divider />
                            <Menu.Item key='export' icon={<TbPackageExport size='1rem' />} onClick={exportToCSV}>
                                Export Results to CSV
                            </Menu.Item>
                        </DropdownMenuPanel>
                    )}
                >
                    <Button
                        size='small'
                        style={{
                            marginRight: '0.25rem',
                        }}
                        type='text'
                        disabled={!hasFeature('dataExport')}
                    >
                        <TbSettings2 style={{ verticalAlign: '-0.125rem', color: token.colorTextTertiary }} />
                    </Button>
                </Dropdown>
            </div>
        </div>
    );
};

export default ListHeader;
